import { HttpParams } from '@angular/common/http'
import { forkJoin } from 'rxjs'
import { Component, DestroyRef, OnDestroy, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faFireFlameCurved, faRotateLeft } from '@fortawesome/free-solid-svg-icons'
import { CollectionViewGroup, Globalize, PropertyGroupDescription, SortDescription } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, DataMap, GridPanel, GroupRow } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    FeatureType,
    Grid,
    ListItem,
    ModalSize,
    PendingChangeOrder,
    VpJob,
    VpUser,
    createPendingChangeOrderInitiatedByDataMap,
    createPendingChangeOrderPricingMethodDataMap,
    createPendingChangeOrderWorkTypeDataMap,
    pendingChangeOrderInitiatedByDisplay,
    pendingChangeOrderPricingMethodDisplay,
    pendingChangeOrderWorkTypeDisplay,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { PendingChangeOrderComponent } from './pending-change-order.component'
import { PendingChangeOrderFieldModalComponent } from './pending-change-order-field-modal.component'
import { PcoDashboardDrillthrough } from './pending-change-orders-dashboard.component'

@Component({
    selector: 'app-pending-change-orders',
    templateUrl: './pending-change-orders.component.html',
    styleUrls: ['./pending-change-orders.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridColumnGroupComponent,
        GridExportComponent,
        GridSelectionCalculationComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
        FaIconComponent,
    ],
})
export class PendingChangeOrdersComponent extends BaseComponent implements OnDestroy {
    private destroyRef = inject(DestroyRef)
    private route = inject(ActivatedRoute)
    private router = inject(Router)
    faFire = faFireFlameCurved
    faRotateLeft = faRotateLeft
    grid: Grid<PendingChangeOrder>

    initiatedByMap = createPendingChangeOrderInitiatedByDataMap()

    workTypeMap = createPendingChangeOrderWorkTypeDataMap()

    pricingMethodMap = createPendingChangeOrderPricingMethodDataMap()

    jobMap: ListItem<string>[] = []
    /** Maps job id → `id + ' ' + name` for grid display (active jobs from `api/VpJob`). */
    jobDisplayMap = new DataMap([], 'value', 'display')
    activeJobs: VpJob[] = []
    estimateCodes: string[] = []
    vistaUserMap = new DataMap([], 'id', 'name')
    /** HR ref → display name, used to resolve a job's project manager. */
    private vistaUserNameById = new Map<number, string>()
    /** Trimmed, lower-cased job id → project manager display name. Includes inactive jobs. */
    private pmNameByJobKey = new Map<string, string>()
    private static readonly noProjectManagerLabel = 'No Project Manager'

    /** Full non-archived PCO list from API; `grid.items` is filtered by Vista import status or the archived list. */
    allItems: PendingChangeOrder[] = []
    archivedItems: PendingChangeOrder[] = []
    /** When false, show open PCOs (no Imported into Vista date). When true, show Vista-complete only. */
    showImported = false
    /** When true, the grid shows soft-archived PCOs instead of the open or imported lists. */
    showSoftArchived = false
    dashboardRecordIds: string[] | null = null
    dashboardFilterLabel: string | null = null
    private pendingDashboardFilter: PcoDashboardDrillthrough | null = null

    readonly isSiteConditionFieldRoute: boolean

    get canEdit() {
        if (this.isSiteConditionFieldRoute) {
            return this.services.authentication.hasFeature([FeatureType.FieldToolsSiteConditionChangeEdit])
        }
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPendingChangeOrdersEdit])
    }

    constructor() {
        super()
        const useVoiceModal = this.route.snapshot.data['voiceModal'] === true
        this.isSiteConditionFieldRoute = useVoiceModal
        const modalComponent = useVoiceModal ? PendingChangeOrderFieldModalComponent : PendingChangeOrderComponent
        const modalTypeName = useVoiceModal ? 'Site Condition Change' : 'Pending Change Order'

        this.grid = new Grid<PendingChangeOrder>(this.services)
            .setupModalEditing(
                this.services.data.pendingChangeOrders,
                modalTypeName,
                modalComponent,
                () => this.createPendingChangeOrderForAdd(),
                { component: this },
                ModalSize.large,
            )
            .setupDeleteConfirm(
                'Confirm Archive',
                'Archive the selected item? It will be hidden from the open and imported lists. You can show archived records and restore it later.',
                'Confirm Archive',
                'Archive the selected items? They will be hidden from the open and imported lists. You can show archived records and restore them later.',
            )
            .wrapColumnHeaders()
            .isReadOnly(false)
            .filterBy(item => [
                item.job,
                this.jobDisplayMap.getDisplayValue(item.job),
                item.projectManagerName,
                item.description,
                item.pcoNumber,
                item.hbNumber,
                pendingChangeOrderInitiatedByDisplay(item.initiatedBy),
                pendingChangeOrderWorkTypeDisplay(item.workType),
                pendingChangeOrderPricingMethodDisplay(item.pricingMethod),
                item.wcdNumber,
                item.notes,
                item.createdByHrRef,
                this.vistaUserMap.getDisplayValue(item.createdByHrRef),
                item.proceedToWorkByHrRef,
                this.vistaUserMap.getDisplayValue(item.proceedToWorkByHrRef),
                item.derivedStage,
                item.daysInStage,
                PendingChangeOrdersComponent.importedIntoVistaDateSearchToken(item),
            ])

        this.grid.modified.on(this.refreshItems.bind(this))
        this.refresh()
    }

    ngOnDestroy() {
        this.grid.modified.off(this.refreshItems)
    }

    refreshItems() {
        const queries = [this.services.data.pendingChangeOrders.queryAll()]
        if (this.showSoftArchived) {
            queries.push(this.archivedQuery())
        }
        const sub = this.services.data
            .load(true, true, ...queries)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    this.allItems = result[0] as PendingChangeOrder[]
                    this.stampProjectManagerNames(this.allItems)
                    if (this.showSoftArchived && result.length > 1) {
                        this.archivedItems = result[1] as PendingChangeOrder[]
                        this.stampProjectManagerNames(this.archivedItems)
                    }
                    this.applyPcoVisibilityFilter()
                    this.tryOpenPcoFromQuery()
                    this.applyPendingDashboardFilter()
                    this.applyDefaultPcoGrouping()
                },
            })
        this.cancelableRequest('', true, sub)
    }

    refresh() {
        const sub = this.services.data
            .load(
                true,
                true,
                this.services.data.pendingChangeOrders.queryAll(),
                this.services.data.get<VpJob[]>('api/VpJob'),
                this.services.data.get<string[]>('api/PendingChangeOrder/GetEstimateCodes'),
                this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                this.services.data.get<VpJob[]>('api/VpJob/GetAllJobs'),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.activeJobs = result[1] as VpJob[]
                    this.buildJobMap()

                    this.estimateCodes = result[2] as string[]

                    const vistaUsers = result[3] as VpUser[]
                    this.vistaUserMap = new DataMap(vistaUsers, 'id', 'name')
                    this.vistaUserNameById = new Map(
                        vistaUsers.filter(user => user?.id != null).map(user => [user.id, user.name]),
                    )
                    this.buildPmNameMap(result[4] as VpJob[])

                    this.allItems = result[0] as PendingChangeOrder[]
                    this.stampProjectManagerNames(this.allItems)
                    this.applyPcoVisibilityFilter()

                    this.initialLoadingComplete()
                    this.tryOpenPcoFromQuery()
                    this.applyPendingDashboardFilter()
                    this.applyDefaultPcoGrouping()
                },
            })
        this.cancelableRequest('', true, sub)
    }

    private applyDefaultPcoGrouping() {
        const cv = this.grid.view
        // Runtime allows null to keep Wijmo's default comparer; IComparer<T> does not.
        cv.sortComparer = ((a: unknown, b: unknown) => {
            if (typeof a === 'string' && typeof b === 'string') {
                return a.trim().localeCompare(b.trim(), undefined, { numeric: true })
            }
            return null
        }) as typeof cv.sortComparer
        cv.sortDescriptions.clear()
        cv.sortDescriptions.push(new SortDescription('job', false))
        cv.groupDescriptions.clear()
        cv.groupDescriptions.push(
            new PropertyGroupDescription('job', (item: PendingChangeOrder) => {
                const display = this.jobDisplayMap.getDisplayValue(item.job)
                return display != null && String(display).length > 0 ? display : item.job ?? ''
            }),
        )
        if (this.grid.isInitialized && this.grid.flexGrid) {
            this.grid.flexGrid.collapseGroupsToLevel(0)
        }
    }

    private tryOpenPcoFromQuery() {
        const openPcoId = this.route.snapshot.queryParamMap.get('openPcoId')
        if (!openPcoId) return
        this.openPcoById(openPcoId, true)
    }

    applyDashboardFilter(filter: PcoDashboardDrillthrough) {
        this.pendingDashboardFilter = filter
        this.applyPendingDashboardFilter()
    }

    clearDashboardFilter() {
        this.pendingDashboardFilter = null
        this.dashboardRecordIds = null
        this.dashboardFilterLabel = null
        this.applyPcoVisibilityFilter()
    }

    private applyPendingDashboardFilter() {
        const filter = this.pendingDashboardFilter
        if (!filter) return

        this.dashboardRecordIds = filter.ids?.map(id => String(id)) ?? null
        this.dashboardFilterLabel = filter.label ?? null
        this.showSoftArchived = false
        this.applyPcoVisibilityFilter()

        if (filter.openPcoId) {
            this.openPcoById(filter.openPcoId, false)
            this.pendingDashboardFilter = { ...filter, openPcoId: undefined }
        }
    }

    private openPcoById(openPcoId: string, clearQueryParam: boolean) {
        const clearParam = () => {
            if (!clearQueryParam) return
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { openPcoId: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            })
        }

        const item = this.allItems.find(i => String(i.id) === openPcoId)
        if (!item) {
            clearParam()
            return
        }

        const wasSoftArchived = this.showSoftArchived
        if (wasSoftArchived) {
            this.showSoftArchived = false
        }

        if (!this.dashboardRecordIds) {
            const vistaComplete = PendingChangeOrdersComponent.hasImportedIntoVistaDate(item)
            if (vistaComplete && !this.showImported) {
                this.showImported = true
            } else if (!vistaComplete && this.showImported) {
                this.showImported = false
            }
        }

        if (wasSoftArchived || !this.dashboardRecordIds) {
            this.applyPcoVisibilityFilter()
        }

        const gridItem = this.grid.items.find(i => String(i.id) === openPcoId)
        if (!gridItem) {
            clearParam()
            return
        }

        this.grid.selectedItem = gridItem
        setTimeout(() => {
            this.grid.editItem(gridItem)
            clearParam()
        }, 0)
    }

    toggleShowImported() {
        if (this.showSoftArchived) {
            this.showSoftArchived = false
            this.showImported = true
            this.applyPcoVisibilityFilter()
            return
        }

        this.showImported = !this.showImported
        this.applyPcoVisibilityFilter()
    }

    toggleShowArchived() {
        if (this.showSoftArchived) {
            this.showSoftArchived = false
            this.applyPcoVisibilityFilter()
            return
        }

        const sub = this.services.data
            .load(true, true, this.archivedQuery())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.archivedItems = result[0] as PendingChangeOrder[]
                    this.stampProjectManagerNames(this.archivedItems)
                    this.showSoftArchived = true
                    this.applyPcoVisibilityFilter()
                },
            })
        this.cancelableRequest('', true, sub)
    }

    restoreSelected() {
        const byId = new Map<string, PendingChangeOrder>()
        for (const row of this.grid.flexGrid?.selectedRows ?? []) {
            const item = row.dataItem as PendingChangeOrder
            if (item?.id) byId.set(String(item.id), item)
        }
        const items = [...byId.values()]
        if (items.length === 0) return

        const message =
            items.length > 1
                ? 'Restore the selected items? They return to the open list, or to Imported into Vista when that date is set.'
                : 'Restore the selected item? It returns to the open list, or to Imported into Vista when that date is set.'

        this.services.modals.confirm('Confirm Restore', message).then(() => {
            const requests = items.map(item =>
                this.services.data.post<PendingChangeOrder>(
                    'api/PendingChangeOrder/Restore',
                    {},
                    new HttpParams().set('id', item.id),
                    PendingChangeOrder,
                ),
            )
            const sub = forkJoin(requests)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.services.notification.success(
                            items.length > 1 ? 'Pending change orders restored' : 'Pending change order restored',
                        )
                        this.refreshItems()
                    },
                    error: () => {
                        this.services.notification.error('Failed to restore one or more pending change orders.')
                        this.refreshItems()
                    },
                })
            this.cancelableRequest('', true, sub)
        })
    }

    private archivedQuery() {
        return this.services.data.get<PendingChangeOrder[]>(
            'api/PendingChangeOrder/GetArchived',
            new HttpParams(),
            PendingChangeOrder,
        )
    }

    private applyPcoVisibilityFilter() {
        if (this.dashboardRecordIds?.length) {
            const idSet = new Set(this.dashboardRecordIds)
            this.grid.items = this.allItems.filter(i => idSet.has(String(i.id)))
            return
        }

        if (this.showSoftArchived) {
            this.grid.items = this.archivedItems
            return
        }

        this.grid.items = this.showImported
            ? this.allItems.filter(i => PendingChangeOrdersComponent.hasImportedIntoVistaDate(i))
            : this.allItems.filter(i => !PendingChangeOrdersComponent.hasImportedIntoVistaDate(i))
    }

    private static hasImportedIntoVistaDate(item: PendingChangeOrder): boolean {
        const raw = item.importedIntoVistaDate
        const dateObj =
            raw instanceof Date ? raw : raw != null && String(raw).length > 0 ? new Date(String(raw)) : null
        return dateObj != null && !Number.isNaN(dateObj.getTime())
    }

    private static importedIntoVistaDateSearchToken(item: PendingChangeOrder): string {
        const raw = item.importedIntoVistaDate
        const dateObj =
            raw instanceof Date ? raw : raw != null && String(raw).length > 0 ? new Date(String(raw)) : null
        if (dateObj == null || Number.isNaN(dateObj.getTime())) return ''
        return Globalize.format(dateObj, 'd')
    }

    buildJobMap() {
        const jobs: ListItem<string>[] = this.activeJobs
            .map(job => new ListItem<string>(job.id, job.id + ' ' + job.name))
            .sort((a, b) => a.display.localeCompare(b.display))

        this.jobMap = jobs.length > 0 ? [new ListItem<string>('', 'Select a Job'), ...jobs] : []

        const jobDisplayEntries = this.activeJobs.map(j => ({
            value: j.id,
            display: j.id + ' ' + j.name,
        }))
        this.jobDisplayMap = new DataMap(jobDisplayEntries, 'value', 'display')
    }

    private buildPmNameMap(jobs: VpJob[]) {
        this.pmNameByJobKey.clear()
        for (const job of jobs) {
            const key = PendingChangeOrdersComponent.jobKey(job.id)
            if (!key) continue
            this.pmNameByJobKey.set(key, this.pmDisplayName(job.pmUserId))
        }
    }

    private stampProjectManagerNames(items: PendingChangeOrder[]) {
        for (const item of items) {
            const key = PendingChangeOrdersComponent.jobKey(item.job)
            item.projectManagerName =
                this.pmNameByJobKey.get(key) ?? PendingChangeOrdersComponent.noProjectManagerLabel
        }
    }

    private pmDisplayName(pmUserId: number | null | undefined): string {
        if (pmUserId == null) return PendingChangeOrdersComponent.noProjectManagerLabel
        const name = this.vistaUserNameById.get(pmUserId)?.trim()
        return name ? name : PendingChangeOrdersComponent.noProjectManagerLabel
    }

    private static jobKey(jobId: string | null | undefined): string {
        return (jobId ?? '').trim().toLowerCase()
    }

    /** Grid columns that show a read-only checkbox + date (same semantics as date-set-today-toggle in the modal). */
    private static readonly dateCheckboxColumnAria: Record<string, { yes: string; no: string }> = {
        ownerAcknowledgedDate: { yes: 'Owner acknowledged', no: 'Owner not acknowledged' },
        readyForApprovalDate: { yes: 'Ready for approval', no: 'Not ready for approval' },
        programMgrApprovalDate: { yes: 'Program manager approval recorded', no: 'No program manager approval date' },
        vpApprovalDate: { yes: 'VP approval recorded', no: 'No VP approval date' },
        sentToOwnerDate: { yes: 'Sent to owner', no: 'Not sent to owner' },
        ownerAuthorizedDate: { yes: 'Owner authorized', no: 'Owner not authorized' },
        ownerExecutedDate: { yes: 'Owner executed', no: 'Owner not executed' },
        importedIntoVistaDate: { yes: 'Imported into Vista', no: 'Not imported into Vista' },
        proceedToWork: { yes: 'Proceed to work recorded', no: 'No proceed to work date' },
    }

    /** Columns shown on Site Condition Change (`/field-tools/pending-change-orders-field`); full grid on Project Management route. */
    private static readonly siteConditionVisiblePcoColumnBindings: ReadonlySet<string> = new Set([
        'job',
        'description',
        'initiatedBy',
        'dateInitiated',
        'createdByHrRef',
        'proceedToWork',
        'notes',
    ])

    isPcoGridColumnVisible(binding: string): boolean {
        if (!this.isSiteConditionFieldRoute) return true
        return PendingChangeOrdersComponent.siteConditionVisiblePcoColumnBindings.has(binding)
    }

    toggleHot(cell) {
        if (this.canEdit) {
            cell.item.isHot = !cell.item.isHot
            this.grid.refresh()
            this.services.data.pendingChangeOrders.edit(cell.item).subscribe({
                next: () => {
                    // Success handling
                },
                error: () => {
                    this.services.notification.error('The pending change order changes were not successfully saved')
                },
            })
        } else {
            this.services.notification.info('You do not have permissions to change this field')
        }
    }

    private createPendingChangeOrderForAdd(): PendingChangeOrder {
        const item = new PendingChangeOrder()
        const job = this.getSelectedJobForAdd()
        if (job) {
            item.job = job
        }
        return item
    }

    private getSelectedJobForAdd(): string | undefined {
        const flex = this.grid.flexGrid
        if (!flex || !this.grid.isInitialized) {
            return undefined
        }

        const sel = flex.selection
        if (sel.row < 0) {
            return undefined
        }

        const row = flex.rows[sel.row]
        if (row instanceof GroupRow) {
            const group = row.dataItem as CollectionViewGroup
            const first = group?.items?.[0] as PendingChangeOrder | undefined
            return first?.job
        }

        const item = row?.dataItem as PendingChangeOrder | undefined
        return item?.job
    }

    mainGridItemFormatter = (panel: GridPanel, r: number, c: number, cell: HTMLDivElement) => {
        if (panel.cellType !== CellType.Cell) return
        cell.style.color = ''
        if (panel.grid.rows[r] instanceof GroupRow) return
        const binding = panel.grid.columns[c].binding as string | null
        if (!binding) return

        if (binding === 'isHot') {
            const item = panel.grid.rows[r]?.dataItem as PendingChangeOrder | undefined
            if (!item) return
            cell.style.color = item.isHot ? 'red' : 'lightgray'
            const icon = cell.children[0]?.children[0]?.children[0]
            if (icon) {
                if (item.isHot) {
                    icon.classList.add('fa-beat-fade')
                } else {
                    icon.classList.remove('fa-beat-fade')
                }
            }
            return
        }

        if (!PendingChangeOrdersComponent.dateCheckboxColumnAria[binding]) return

        const row = panel.grid.rows[r]
        const item = row?.dataItem as PendingChangeOrder | undefined
        if (!item) return

        const raw = item[binding as keyof PendingChangeOrder]
        const dateObj =
            raw instanceof Date ? raw : raw != null && String(raw).length > 0 ? new Date(String(raw)) : null
        const hasDate = dateObj != null && !Number.isNaN(dateObj.getTime())
        const aria = PendingChangeOrdersComponent.dateCheckboxColumnAria[binding]

        cell.innerHTML = ''
        const wrap = document.createElement('span')
        wrap.className = 'pending-co-date-cell-inner'

        const cb = document.createElement('input')
        cb.type = 'checkbox'
        cb.disabled = true
        cb.checked = hasDate
        cb.tabIndex = -1
        cb.setAttribute('aria-label', hasDate ? aria.yes : aria.no)
        wrap.appendChild(cb)

        if (hasDate && dateObj) {
            const dateSpan = document.createElement('span')
            dateSpan.className = 'pending-co-date-cell-date'
            dateSpan.textContent = Globalize.format(dateObj, 'd')
            wrap.appendChild(dateSpan)
        }

        cell.appendChild(wrap)
    }
}
